import React from 'react';
import { cn } from '@/lib/utils';
import BaseProductViewer, { BaseProductViewerProps } from '../base-product-viewer';
import Image from 'next/image';

// Define the ProductViewer props that extend the BaseProductViewer props
export interface ProductViewerProps extends BaseProductViewerProps {
  // Additional properties specific to the design system
  colorTheme?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'danger';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'outline' | 'glass' | 'solid';
  badgeText?: string;
  productName?: string;
  productDescription?: string;
  spotlight?: boolean;
  info?: string;
  imageFallback?: {
    src: string;
    width: number;
    height: number;
    alt: string;
    blurDataURL?: string;
  };
  forceImageFallback?: boolean;
}

// Map color theme names to actual colors
const getThemeColors = (theme: ProductViewerProps['colorTheme'], variant: ProductViewerProps['variant']) => {
  // Base colors
  const themeMap = {
    primary: {
      light: '#3b82f6', // blue-500
      dark: '#2563eb', // blue-600
      glass: 'rgba(59, 130, 246, 0.08)' // blue-500 with reduced opacity
    },
    secondary: {
      light: '#8b5cf6', // violet-500
      dark: '#7c3aed', // violet-600
      glass: 'rgba(139, 92, 246, 0.08)' // violet-500 with reduced opacity
    },
    accent: {
      light: '#ec4899', // pink-500
      dark: '#db2777', // pink-600
      glass: 'rgba(236, 72, 153, 0.08)' // pink-500 with reduced opacity
    },
    info: {
      light: '#06b6d4', // cyan-500
      dark: '#0891b2', // cyan-600
      glass: 'rgba(6, 182, 212, 0.08)' // cyan-500 with reduced opacity
    },
    success: {
      light: '#10b981', // emerald-500
      dark: '#059669', // emerald-600
      glass: 'rgba(16, 185, 129, 0.08)' // emerald-500 with reduced opacity
    },
    warning: {
      light: '#f59e0b', // amber-500
      dark: '#d97706', // amber-600
      glass: 'rgba(245, 158, 11, 0.08)' // amber-500 with reduced opacity
    },
    danger: {
      light: '#ef4444', // red-500
      dark: '#dc2626', // red-600
      glass: 'rgba(239, 68, 68, 0.08)' // red-500 with reduced opacity
    }
  };

  // Default to primary if theme not specified
  const selectedTheme = theme ? themeMap[theme] : themeMap.primary;
  
  // Return appropriate color based on variant
  switch (variant) {
    case 'outline':
    case 'glass':
      return selectedTheme.glass;
    case 'solid':
      return selectedTheme.dark;
    default:
      return selectedTheme.light;
  }
};

// Apply the design system border radius to the component
const getRadiusClass = (radius: ProductViewerProps['radius']) => {
  // If a specific radius is provided, use that, otherwise use the component default
  return radius ? `premium-radius-${radius}` : 'premium-radius-md';
};

// Apply shadow from the design system
const getShadowClass = (shadow: ProductViewerProps['shadow']) => {
  if (shadow === 'none') return '';
  return shadow ? `premium-shadow-${shadow}` : 'premium-shadow-md';
};

// ProductViewer Component with adapter pattern
const ProductViewer = React.forwardRef<HTMLDivElement, ProductViewerProps>(
  ({
    colorTheme = 'primary',
    radius = 'md',
    shadow = 'sm',
    variant = 'default',
    badgeText,
    productName,
    productDescription,
    spotlight = false,
    className,
    info,
    imageFallback,
    forceImageFallback = false,
    ...props
  }, ref) => {
    // Generate classes based on design system tokens
    const radiusClass = getRadiusClass(radius);
    const shadowClass = getShadowClass(shadow);
    const themeColor = getThemeColors(colorTheme, variant);
    
    // Create a state to track whether the 3D model failed to load
    const [modelFailed, setModelFailed] = React.useState(false);
    
    // Handle model load error
    React.useEffect(() => {
      const handleModelError = () => {
        console.log("Model load error detected, switching to fallback image");
        setModelFailed(true);
      };
      
      window.addEventListener('model-load-error', handleModelError);
      
      // Also try to fetch the model directly to check if it exists
      const checkModelExists = async () => {
        try {
          if (props.modelPath) {
            const response = await fetch(props.modelPath, { method: 'HEAD' });
            if (!response.ok) {
              console.log(`Model not found at ${props.modelPath}, using fallback`);
              setModelFailed(true);
            }
          }
        } catch (error) {
          console.log("Error checking model:", error);
          setModelFailed(true);
        }
      };
      
      checkModelExists();
      
      return () => {
        window.removeEventListener('model-load-error', handleModelError);
      };
    }, [props.modelPath]);
    
    // Create the info overlay if product details are provided
    const infoOverlay = (productName || productDescription) ? (
      <div className="overlay product-info-overlay">
        {productName && <h3 className="text-xs font-medium">{productName}</h3>}
        {productDescription && <p className="text-xs opacity-75 text-gray-600 dark:text-gray-300">{productDescription}</p>}
      </div>
    ) : undefined;

    // Custom environment based on theme color
    const environmentPreset = 'warehouse'; // Use a simple preset that doesn't require HDR files

    // Combine styles for the container
    const containerClasses = cn(
      'product-viewer-container',
      'relative',
      variant === 'outline' ? `border border-${colorTheme}-200 dark:border-${colorTheme}-800` : '',
      variant === 'glass' ? 'backdrop-blur-[2px] bg-white/20 dark:bg-black/20' : '',
      radiusClass,
      shadowClass,
      className
    );

    // If we have an image fallback and model failed or forceImageFallback is true, show the image
    if (imageFallback && (modelFailed || forceImageFallback)) {
      console.log(`Rendering fallback image: ${imageFallback.src}`);
      return (
        <div
          ref={ref}
          className={cn(containerClasses, 'w-full h-full')}
          style={{
            background: variant === 'glass' ? themeColor : undefined,
            overflow: 'hidden',
            position: 'relative' // Ensure relative positioning for the image
          }}
          onClick={props.onModelClick}
        >
          {/* Badge if provided */}
          {badgeText && (
            <div
              className={`absolute top-1.5 right-1.5 z-20 px-2 py-0.5 text-xs font-bold tracking-wide
                          backdrop-blur-sm shadow-sm
                          ${variant === 'solid'
                            ? 'bg-white/95 text-red-600'
                            : 'bg-red-500/95 text-white'}
                          rounded-md`}
            >
              {badgeText}
            </div>
          )}

          {/* Render the actual fallback image */}
          <Image
            {...imageFallback}
            quality={85}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="w-full h-full object-contain"
            placeholder={imageFallback.blurDataURL ? "blur" : undefined}
            onError={(e) => console.error(`Error loading fallback image ${imageFallback.src}:`, e)}
          />

          {/* Optional: You can add product name/description overlay on top of the image if needed */}
          {(productName || productDescription) && (
            <div className="overlay absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent z-10">
              {productName && <h3 className="text-xs font-medium text-white truncate">{productName}</h3>}
              {productDescription && <p className="text-xs opacity-80 text-white truncate">{productDescription}</p>}
            </div>
          )}
        </div>
      );
    }

    // If no fallback needed or available, attempt to render the 3D model
    console.log("Attempting to render 3D model:", props.modelPath);

    return (
      <div 
        ref={ref} 
        className={containerClasses}
        style={{ 
          background: variant === 'glass' ? themeColor : undefined,
          overflow: 'hidden'
        }}
      >
        {/* Badge if provided */}
        {badgeText && (
          <div
            className={`absolute top-1.5 right-1.5 z-20 px-2 py-0.5 text-xs font-bold tracking-wide
                        backdrop-blur-sm shadow-sm
                        ${variant === 'solid'
                          ? 'bg-white/95 text-red-600'
                          : 'bg-red-500/95 text-white'}
                        rounded-md`}
          >
            {badgeText}
          </div>
        )}

        {/* Info tooltip if provided */}
        {info && (
          <div className="absolute bottom-1.5 right-1.5 z-10">
            <div className="group relative">
              <button 
                className={`rounded-full w-5 h-5 flex items-center justify-center text-xs
                           bg-gray-200 text-gray-600 hover:bg-gray-300
                           dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600`}
              >
                i
              </button>
              <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover:block w-36 p-1.5 bg-white/90 dark:bg-gray-800/90 shadow-sm rounded text-xs">
                {info}
              </div>
            </div>
          </div>
        )}

        {/* Spotlight effect */}
        {spotlight && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 50% 50%, 
                           ${themeColor}33 0%, 
                           transparent 60%)`,
              mixBlendMode: 'overlay'
            }}
          />
        )}

        {/* Pass all remaining props to the base component */}
        <React.Suspense fallback={
          <div className="w-full h-full flex items-center justify-center">
            <div className="loading-spinner">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
        }>
          <BaseProductViewer
            className={`w-full h-full ${props.isSelected ? 'ring-1 ring-offset-1 ring-offset-white dark:ring-offset-black ring-' + colorTheme + '-500' : ''}`}
            environmentPreset={environmentPreset}
            infoOverlay={infoOverlay}
            onError={() => setModelFailed(true)}
            {...props}
          />
        </React.Suspense>
      </div>
    );
  }
);

ProductViewer.displayName = 'ProductViewer';

export { ProductViewer }; 